// Единственная дверь к серверу. Всё, что уходит по сети, проходит здесь: подпись токеном,
// ключ идемпотентности, разбор конверта ошибки, единая точка реакции на 401.

import { ApiError } from '@/shared/errors/api-error';
import { useAuthStore } from '@/shared/auth/store';

import { serverAddress } from './server-address';
import type { ErrorEnvelope } from './types';

// Адрес сервера живёт в отдельном модуле: он настраивается ПОСЛЕ установки программы.
// Собранный один раз установщик ставится и на демонстрацию, и на боевой контур заказчика, а
// адрес, зашитый в сборку, означал бы новую сборку на каждый переезд сервера.
export { serverAddress as apiBaseUrl } from './server-address';

/**
 * Обработчик 401. Реализация живёт в shared/auth/session.ts (single-flight обновление),
 * а регистрируется сюда, чтобы не завязывать клиент на модуль сессии кольцом импортов:
 * session -> endpoints/auth -> client.
 */
type RefreshHandler = () => Promise<boolean>;
let refreshHandler: RefreshHandler | null = null;

export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Тело запроса; сериализуется в JSON. */
  body?: unknown;
  /**
   * Тело формой: снимок чека и подобное. Взаимоисключающе с `body`.
   *
   * `Content-Type` для формы НЕ ставится вручную: браузер добавляет к нему границу частей
   * (`boundary`), и заголовок, написанный руками, эту границу теряет — сервер получает
   * неразбираемое тело и отвечает «поле file обязательно» на запрос, где файл есть.
   */
  form?: FormData;
  /** Ключ идемпотентности: сервер на повтор возвращает ранее созданный результат. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** false — запрос без подписи токеном (вход, обновление). По умолчанию подписываем. */
  auth?: boolean;
  /** false — не пытаться обновить сессию при 401. Обязателен для самих auth-эндпоинтов. */
  retryOn401?: boolean;
}

/**
 * Отказ `fetch` — это ТРИ РАЗНЫХ факта, и до 21.09.2026 все три назывались одним словом.
 *
 * Живой случай: заказчик пять раз нажал «Создать ведомости за июль», сервер пять раз ответил
 * 500 за доли секунды (видно в журнале обращений), а на экране стояло «нет связи с сервером —
 * проверьте сеть и повторите». Сеть была. Ответ 500 уходил мимо слоя доступа, браузер резал
 * его до программы, `fetch` отклонялся — и программа честно пересказывала то, что ей
 * показали. Настоящей причины не видел никто трое суток, а совет «проверьте сеть» отправлял
 * человека чинить исправное.
 *
 * Серверная половина закрыта (`server/app/main.py`), но отказ `fetch` останется возможным
 * всегда — и он не имеет права врать про сеть, когда сеть есть.
 */
function отказСети(запрос: string): ErrorEnvelope {
  // `navigator.onLine` отвечает на один вопрос — есть ли у машины хоть какая-то сеть. Врать
  // в сторону «есть» он может (вайфай без интернета), в сторону «нет» — нет: если браузер
  // говорит, что сети нет, её действительно нет.
  const сетиНет = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (сетиНет) {
    return {
      code: 'network_offline',
      message: 'нет связи с сервером',
      hint: 'компьютер сейчас без сети',
      next_action: 'проверьте сеть и повторите',
    };
  }
  return {
    code: 'network_error',
    message: 'ответ от сервера не пришёл',
    hint: `сеть на месте, но запрос «${запрос}» оборвался. Так выглядит и сбой на сервере, и обрыв посередине`,
    next_action: 'обновите экран и посмотрите, не сделалось ли уже; если нет — повторите',
  };
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await performRequest(path, options, options.retryOn401 !== false);
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Загрузка файла формой. Идёт через ТУ ЖЕ дверь, что и всё остальное: подпись токеном, единая
 * точка 401 и разбор конверта ошибки одинаковы. Второй `fetch` рядом означал бы вторую политику
 * повторов и вторую обработку отказа — и разъехались бы они на первой правке.
 */
export async function requestUpload<T>(
  path: string,
  form: FormData,
  options: Omit<RequestOptions, 'body' | 'form'> = {},
): Promise<T> {
  const response = await performRequest(
    path,
    { ...options, form, method: options.method ?? 'POST' },
    options.retryOn401 !== false,
  );
  return (await response.json()) as T;
}

/** Файл с сервера: имя берётся из заголовка вложения, чтобы не выдумывать его на клиенте. */
export interface FileResponse {
  blob: Blob;
  filename: string;
}

/**
 * Выгрузка файла. Идёт через ту же дверь, что и всё остальное: подпись токеном и разбор
 * конверта ошибки одинаковы. Прямой `window.open` на адрес выгрузки токен не понесёт и
 * вернёт человеку страницу отказа вместо файла.
 */
export async function requestFile(
  path: string,
  options: RequestOptions = {},
  fallbackFilename = 'file',
): Promise<FileResponse> {
  const response = await performRequest(path, options, options.retryOn401 !== false);
  return {
    blob: await response.blob(),
    filename:
      filenameFromDisposition(response.headers.get('Content-Disposition')) ?? fallbackFilename,
  };
}

function filenameFromDisposition(header: string | null): string | null {
  if (header === null) {
    return null;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1] !== undefined) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Битая кодировка имени — не повод ронять выгрузку: возьмём запасное имя.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

async function performRequest(
  path: string,
  options: RequestOptions,
  allowRefreshRetry: boolean,
): Promise<Response> {
  const headers = new Headers();
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.auth !== false) {
    const token = useAuthStore.getState().accessToken;
    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  if (options.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', options.idempotencyKey);
  }

  let response: Response;
  try {
    response = await fetch(`${serverAddress()}${path}`, {
      method:
        options.method ??
        (options.body !== undefined || options.form !== undefined ? 'POST' : 'GET'),
      headers,
      body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    });
  } catch (cause) {
    // Отмену запроса не переодеваем в сетевую ошибку: её попросил сам вызывающий код.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new ApiError(отказСети(path), null);
  }

  // Единая точка 401: одно single-flight обновление сессии и ровно один повтор запроса.
  if (response.status === 401 && allowRefreshRetry && options.auth !== false && refreshHandler) {
    const refreshed = await refreshHandler();
    if (refreshed) {
      return performRequest(path, options, false);
    }
  }

  if (!response.ok) {
    throw new ApiError(await parseErrorEnvelope(response), response.status);
  }

  return response;
}

/** Разбирает конверт сервера; на неожиданной форме тела строит синтетический конверт. */
async function parseErrorEnvelope(response: Response): Promise<ErrorEnvelope> {
  try {
    const body: unknown = await response.json();
    if (
      typeof body === 'object' &&
      body !== null &&
      typeof (body as { code?: unknown }).code === 'string' &&
      typeof (body as { message?: unknown }).message === 'string'
    ) {
      return body as unknown as ErrorEnvelope;
    }
  } catch {
    // Тело не JSON — например, ответ прокси. Падаем в синтетический конверт ниже.
  }
  return {
    code: `http_${response.status}`,
    message: 'сервер ответил ошибкой без пояснения',
    next_action: 'повторите действие; не помогло — сообщите администратору',
  };
}
